"use client";

import {
    createContext,
    useContext,
    useEffect,
    useState,
    ReactNode,
    useMemo,
    useCallback,
} from "react";

/* =========================
   TYPES
========================= */

export type RestaurantPermission = {
    can_cancel_reservations: boolean;
};

export type SubscriptionFeatures = {
    has_confirmations: boolean;
    has_reminders: boolean;
    has_daily_reports: boolean;
    has_vip_marking: boolean;
    has_custom_emails: boolean;
    has_api_access: boolean;
    has_custom_domain: boolean;
};

export type SubscriptionInfo = {
    plan_code: string;
    plan_name: string;
    status: string;
    billing_cycle: string;
    trial_ends_at?: string;
    current_period_end?: string;
    features: SubscriptionFeatures;
};

export type SubscriptionAccess = {
    allowed: boolean;
    reason: string;
    status: string;
    days_remaining: number | null;
};

export type RestaurantAccess = {
    id: number;
    name: string;
    slug: string;
    logo_url?: string | null;
    language: string;
    role: "OWNER" | "STAFF";
    permissions: RestaurantPermission;
    thank_you_email_enabled?: boolean;
    allow_reservation_language?: boolean;
    subscription?: SubscriptionInfo;
    subscription_access?: SubscriptionAccess;
    onboarding_completed?: boolean;
};

export type User = {
    id: number;
    email: string;
    // Datos editables
    first_name?: string;
    last_name?: string;
    phone?: string;

    // Compatibilidad
    name?: string;
    is_superadmin: boolean;
};

type AuthContextType = {
    user: User | null;
    restaurants: RestaurantAccess[];
    activeRestaurant: RestaurantAccess | null;

    loading: boolean;
    ready: boolean;

    isSuperAdmin: boolean;
    isOwner: boolean;
    isStaff: boolean;
    canCancelReservations: boolean;

    setActiveRestaurant: (slug: string) => void;
    refreshUser: () => Promise<void>;
    logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/* =========================
   PROVIDER
========================= */

export function AuthProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [restaurants, setRestaurants] = useState<RestaurantAccess[]>([]);
    const [activeRestaurantSlug, setActiveRestaurantSlug] =
        useState<string | null>(null);

    const [loading, setLoading] = useState(true);
    const [ready, setReady] = useState(false);

    /* =========================
       FETCH /api/me
    ========================= */

    const fetchUser = useCallback(async () => {
        // 🔒 BLOQUEO TOTAL DE UI
        setLoading(true);
        setReady(false);

        // ⛔️ INVALIDAR ESTADO ANTERIOR
        setUser(null);
        setRestaurants([]);
        setActiveRestaurantSlug(null);

        try {
            const res = await fetch("/api/me", {
                credentials: "include",
                cache: "no-store",
                headers: {
                    "Cache-Control": "no-store",
                },
            });

            if (!res.ok) {
                throw new Error("Not authenticated");
            }

            const data = await res.json();

            setUser(data.user);
            setRestaurants(data.restaurants ?? []);

            if (data.restaurants?.length) {
                setActiveRestaurantSlug(data.restaurants[0].slug);
            }
        } catch {
            setUser(null);
            setRestaurants([]);
            setActiveRestaurantSlug(null);
        } finally {
            setLoading(false);
            setReady(true);
        }
    }, []);

    // 🚀 Ejecutar SOLO al montar
    useEffect(() => {
        fetchUser();
    }, [fetchUser]);

    /* =========================
       RESTAURANTE ACTIVO
    ========================= */

    const activeRestaurant = useMemo(() => {
        if (!activeRestaurantSlug) return null;
        return restaurants.find(r => r.slug === activeRestaurantSlug) || null;
    }, [restaurants, activeRestaurantSlug]);

    /* =========================
       HELPERS
    ========================= */

    const isSuperAdmin = !!user?.is_superadmin;
    const isOwner = activeRestaurant?.role === "OWNER";
    const isStaff = activeRestaurant?.role === "STAFF";

    const canCancelReservations =
        isSuperAdmin ||
        activeRestaurant?.permissions?.can_cancel_reservations === true;

    /* =========================
       LOGOUT
    ========================= */

    const logout = async () => {
        try {
            await fetch("/api/auth/logout", { method: "POST" });
        } catch { }

        // ⛔️ LIMPIEZA TOTAL
        setUser(null);
        setRestaurants([]);
        setActiveRestaurantSlug(null);
        setReady(false);

        window.location.href = "/login";
    };

    return (
        <AuthContext.Provider
            value={{
                user,
                restaurants,
                activeRestaurant,
                loading,
                ready,
                isSuperAdmin,
                isOwner,
                isStaff,
                canCancelReservations,
                setActiveRestaurant: setActiveRestaurantSlug,
                refreshUser: fetchUser,
                logout,
            }}
        >
            {children}
        </AuthContext.Provider>
    );
}

/* =========================
   HOOK
========================= */

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) {
        throw new Error("useAuth debe usarse dentro de AuthProvider");
    }
    return ctx;
}